import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with interceptors for authentication
const apiClient = axios.create();

// Request interceptor - Add Authorization header to all requests
apiClient.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle 401 and 403 errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // 401 Unauthorized - Token expired or invalid
      if (error.response.status === 401) {
        sessionStorage.removeItem('auth_token');
        window.location.href = '/login';
      }
      
      // 403 Forbidden - No permission
      if (error.response.status === 403) {
        // You can handle this globally or let individual components handle it
        console.error('Access denied:', error.response.data?.detail);
      }
    }
    return Promise.reject(error);
  }
);

export const api = {
  // Dashboard
  getDashboardStats: () => apiClient.get(`${API}/dashboard/stats`),

  // SSO — mint a CR-signed JWT for handoff to another 1OS engine
  issueSsoToken: (data) => apiClient.post(`${API}/auth/sso/issue-token`, data),

  // Organizations
  getOrganizations: () => apiClient.get(`${API}/orgs`),
  getOrgModules: (orgId) => apiClient.get(`${API}/orgs/${orgId}/modules`),
  setOrgModule: (orgId, moduleCode, isEnabled) => apiClient.put(`${API}/orgs/${orgId}/modules/${moduleCode}`, { is_enabled: isEnabled }),
  getOrgModuleAudit: (orgId, moduleCode) => apiClient.get(`${API}/orgs/${orgId}/modules/${moduleCode}/audit`),
  // Organisation → Applications (canonical app registry + per-user access)
  // Canonical application registry (shared catalogue, no hardcoded product lists)
  getApplications: () => apiClient.get(`${API}/applications`),
  getOrgApplications: (orgId) => apiClient.get(`${API}/orgs/${orgId}/applications`),
  getOrgApplicationUsers: (orgId, appCode) => apiClient.get(`${API}/orgs/${orgId}/applications/${appCode}/users`),
  // One user's effective access to every canonical application (Users & Roles panel)
  getUserApplications: (orgId, userId) => apiClient.get(`${API}/orgs/${orgId}/users/${userId}/applications`),
  setOrgApplicationUserAccess: (orgId, appCode, userId, enabled) =>
    apiClient.put(`${API}/orgs/${orgId}/applications/${appCode}/users/${userId}`, { enabled }),
  createOrganization: (data) => apiClient.post(`${API}/orgs`, data),
  getOrganization: (id) => apiClient.get(`${API}/orgs/${id}`),
  updateOrganization: (id, data) => apiClient.patch(`${API}/orgs/${id}`, data),
  deleteOrganization: (id) => apiClient.delete(`${API}/orgs/${id}`, {
    headers: { 'X-Confirm-Delete': 'DELETE_ORG' }
  }),

  // Outlets
  getOutlets: (orgId) => apiClient.get(`${API}/outlets`, { params: { org_id: orgId } }),
  createOutlet: (data) => apiClient.post(`${API}/outlets`, data),
  getOutlet: (id) => apiClient.get(`${API}/outlets/${id}`),
  updateOutlet: (id, data) => apiClient.patch(`${API}/outlets/${id}`, data),
  updateOutletTheme: (id, themeId, themeMode) =>
    apiClient.patch(`${API}/outlets/${id}/theme?theme_id=${themeId}&theme_mode=${themeMode}`),
  deleteOutlet: (id) => apiClient.delete(`${API}/outlets/${id}`),

  // Users
  getUsers: () => apiClient.get(`${API}/users`),
  createUser: (data) => apiClient.post(`${API}/users`, data),
  getUser: (id) => apiClient.get(`${API}/users/${id}`),
  updateUser: (id, data) => apiClient.patch(`${API}/users/${id}`, data),
  changePassword: (id, newPassword) => apiClient.post(`${API}/users/${id}/change-password`, { new_password: newPassword }),
  getUserAllowedApps: (id) => apiClient.get(`${API}/users/${id}/allowed-apps`),
  getAllUsersAllowedApps: () => apiClient.get(`${API}/users/allowed-apps/all`),
  deleteUser: (id) => apiClient.delete(`${API}/users/${id}`),

  // User Preferences
  getUserPreferences: () => apiClient.get(`${API}/me/preferences`),
  updateUserPreferences: (data) => apiClient.patch(`${API}/me/preferences`, data),

  // Roles
  getRoles: () => apiClient.get(`${API}/roles`),
  getUserRoles: (params) => apiClient.get(`${API}/user-roles`, { params }),
  assignUserRole: (data) => apiClient.post(`${API}/user-roles`, data),

  // Themes
  getThemes: () => apiClient.get(`${API}/themes`),
  createTheme: (data) => apiClient.post(`${API}/themes`, data),
  getTheme: (id) => apiClient.get(`${API}/themes/${id}`),

  // Global Masters
  getVendors: (q) => apiClient.get(`${API}/vendors`, { params: { q } }),
  getItems: (q) => apiClient.get(`${API}/items`, { params: { q } }),
  getIngredients: () => apiClient.get(`${API}/ingredients`),

  // Bills: canonical source is the Bills service, read through
  // GET /api/external/bills (organisation-scoped). The legacy local bill store
  // routes are retired.

  // External Data (synced from external API)
  getExternalVendors: () => apiClient.get(`${API}/external/vendors`),
  getExternalBills: () => apiClient.get(`${API}/external/bills`),
  getBillDetails: (billId) => apiClient.get(`${API}/external/bills/${billId}`),
  getExternalItems: () => apiClient.get(`${API}/external/items`),
  getExternalIngredients: () => apiClient.get(`${API}/external/ingredients`),
  getVendorMetrics: (vendorId) => apiClient.get(`${API}/external/vendor-metrics`, { params: { vendor_id: vendorId } }),
  
  // Trial Analytics
  getTrialsSummary: (params) => apiClient.get(`${API}/trials/summary`, { params }),
  
  // Discount Computation (Control Room computes from MRP and unit_price)
  getDiscountSummary: () => apiClient.get(`${API}/discounts/summary`),
  getVendorDiscountDetail: (vendorId) => apiClient.get(`${API}/discounts/vendor/${vendorId}`),
  computeDiscounts: () => apiClient.post(`${API}/discounts/compute`),
  
  // Deletion Approvals — Accounts Engine only.
  // RETIRED: Supplier Bill delete governance (getDeleteRequests / approveDeleteRequest /
  // rejectDeleteRequest / executeDeleteRequest) was removed. 1OS Bills owns Supplier Bill VOID;
  // Control Room has no operational entry point and does not proxy to Bills.
  // Historical CR records (read-only): GET /api/delete-requests/history

  // Integration Health Check
  getIntegrationsHealth: () => apiClient.get(`${API}/integrations/health`),

  // Canonical Ingredient Master (P3 - Control Room owns Ingredient Master)
  getCanonicalIngredients: (params) => apiClient.get(`${API}/canonical-ingredients`, { params }),
  getCanonicalIngredient: (id) => apiClient.get(`${API}/canonical-ingredients/${id}`),
  createCanonicalIngredient: (data) => apiClient.post(`${API}/canonical-ingredients`, data),
  updateCanonicalIngredient: (id, data) => apiClient.patch(`${API}/canonical-ingredients/${id}`, data),
  mergeIngredients: (targetId, sourceIds) => apiClient.post(`${API}/canonical-ingredients/${targetId}/merge`, { source_ingredient_ids: sourceIds }),
  bulkUploadIngredients: (formData) => apiClient.post(`${API}/canonical-ingredients/bulk-upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  exportCanonicalIngredients: (since) => apiClient.get(`${API}/canonical-ingredients/export`, { params: { since } }),

  // Excel Column Mapping Engine
  detectExcelColumns: (formData) => apiClient.post(`${API}/excel/detect-columns`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  previewImport: (data) => apiClient.post(`${API}/excel/preview-import`, data),
  resolveExcelRows: (data) => apiClient.post(`${API}/excel/resolve-rows`, data),
  importMappedIngredients: (data) => apiClient.post(`${API}/ingredients/import-mapped`, data),
  // V2 patch flow (UPDATE/UPSERT for existing ingredients)
  previewIngredientImport: (data) => apiClient.post(`${API}/ingredients/import-preview`, data),
  commitIngredientImport: (data) => apiClient.post(`${API}/ingredients/import-commit`, data),
  getMappingTemplates: (entityType = 'ingredient') => apiClient.get(`${API}/mapping-templates`, { params: { entity_type: entityType } }),
  saveMappingTemplate: (data) => apiClient.post(`${API}/mapping-templates`, data),
  deleteMappingTemplate: (templateName) => apiClient.delete(`${API}/mapping-templates/${encodeURIComponent(templateName)}`),

  // Ingredient Price Stats (Computed from Vendor App mapped bill items)
  getIngredientPriceStats: (ingredientId) => apiClient.get(`${API}/ingredients/${ingredientId}/price-stats`),
  getIngredientPricing: (ingredientId) => apiClient.get(`${API}/ingredients/${ingredientId}/pricing`),
  recomputeAllPriceStats: () => apiClient.post(`${API}/ingredients/price-stats/recompute`),
  recomputeIngredientPriceStats: (ingredientId) => apiClient.post(`${API}/ingredients/${ingredientId}/price-stats/recompute`),

  // Price Drift Alerts
  getPriceDriftAlerts: (params) => apiClient.get(`${API}/price-drift-alerts`, { params }),
  getIngredientDriftAlerts: (ingredientId, limit = 10) => apiClient.get(`${API}/price-drift-alerts/ingredient/${ingredientId}`, { params: { limit } }),
  acknowledgeDriftAlert: (alertId) => apiClient.patch(`${API}/price-drift-alerts/${alertId}/acknowledge`),
  resolveDriftAlert: (alertId, notes) => apiClient.patch(`${API}/price-drift-alerts/${alertId}/resolve`, { notes }),
  triggerDriftScan: (daysBack = 7) => apiClient.post(`${API}/price-drift-alerts/scan`, null, { params: { days_back: daysBack } }),

  // Ingredient Proposals (P3 - Vendor Engine proposes, Control Room approves)
  getIngredientProposals: (params) => apiClient.get(`${API}/ingredient-proposals`, { params }),
  createIngredientProposal: (data) => apiClient.post(`${API}/ingredient-proposals`, data),
  approveIngredientProposal: (id, action, mergeIntoId) => apiClient.post(`${API}/ingredient-proposals/${id}/approve`, { 
    action, 
    merge_into_ingredient_id: mergeIntoId 
  }),
  rejectIngredientProposal: (id, reason) => apiClient.post(`${API}/ingredient-proposals/${id}/reject`, { reason }),

  // Insights
  getInsights: (params) => apiClient.get(`${API}/insights`, { params }),
  getInsight: (id) => apiClient.get(`${API}/insights/${id}`),
  updateInsightStatus: (id, status) => apiClient.patch(`${API}/insights/${id}/status`, { status }),
  getInsightsSummary: (params) => apiClient.get(`${API}/insights/stats/summary`, { params }),
  generateSampleInsights: () => apiClient.post(`${API}/insights/generate-samples`),
  getInsightTypes: () => apiClient.get(`${API}/insight-types`),

  // Configuration Management
  getEngineConfigs: (engineName) => apiClient.get(`${API}/engine-configs`, { params: { engine_name: engineName } }),
  getEngineDefaults: () => apiClient.get(`${API}/engine-configs/defaults`),
  initializeEngineConfigs: () => apiClient.post(`${API}/engine-configs/initialize`),
  updateEngineConfig: (configId, data) => apiClient.patch(`${API}/engine-configs/${configId}`, data),

  // Settings Management (Org/Outlet level configuration)
  getSettingsDefaults: () => apiClient.get(`${API}/settings/defaults`),
  getOrgSettings: () => apiClient.get(`${API}/settings/org`),
  updateOrgSettings: (data) => apiClient.put(`${API}/settings/org`, data),
  resetOrgSetting: (settingKey) => apiClient.delete(`${API}/settings/org/${settingKey}`),
  getOutletSettings: (outletId) => apiClient.get(`${API}/settings/outlet/${outletId}`),
  updateOutletSettings: (outletId, data) => apiClient.put(`${API}/settings/outlet/${outletId}`, data),
  resetOutletSetting: (outletId, settingKey) => apiClient.delete(`${API}/settings/outlet/${outletId}/${settingKey}`),
  getResolvedSettings: (outletId) => apiClient.get(`${API}/settings/resolved`, { params: { outlet_id: outletId } }),
  listOutletSettings: () => apiClient.get(`${API}/settings/outlets`),

  // Audit Logs
  getAuditLogs: (params) => apiClient.get(`${API}/audit-logs`, { params }),
  getAuditStats: (params) => apiClient.get(`${API}/audit-logs/stats`, { params }),

  // Vendor Mapping Memory (P2 - Learn from manual ingredient mappings)
  getVendorMappings: (params) => apiClient.get(`${API}/vendor-mappings`, { params }),
  createVendorMapping: (data) => apiClient.post(`${API}/vendor-mappings`, data),
  updateVendorMapping: (id, data) => apiClient.patch(`${API}/vendor-mappings/${id}`, data),
  deleteVendorMapping: (id) => apiClient.delete(`${API}/vendor-mappings/${id}`),
  suggestMapping: (vendorId, itemName) => apiClient.get(`${API}/vendor-mappings/suggest`, { 
    params: { vendor_id: vendorId, item_name: itemName } 
  }),
  bulkCreateMappings: (mappings) => apiClient.post(`${API}/vendor-mappings/bulk`, { mappings }),
  getVendorMappingStats: (vendorId) => apiClient.get(`${API}/vendor-mappings/stats`, { 
    params: { vendor_id: vendorId } 
  }),

  // Tax explanation retired with the legacy local bill store (owner: BILLS)

  // =========================================================================
  // ACCESS CONTROL APIs (Phase 1 - Outlet-First Authorization)
  // =========================================================================
  
  // Departments
  getDepartments: (params) => apiClient.get(`${API}/access/departments`, { params }),
  createDepartment: (data) => apiClient.post(`${API}/access/departments`, data),
  updateDepartment: (id, data) => apiClient.patch(`${API}/access/departments/${id}`, data),
  
  // User Scope Assignments (CRITICAL)
  getUserScope: (userId) => apiClient.get(`${API}/access/users/${userId}/scopes`),
  updateUserScope: (userId, data) => apiClient.put(`${API}/access/users/${userId}/scopes`, data),
  
  // User Module Access
  getUserModules: (userId) => apiClient.get(`${API}/access/users/${userId}/modules`),
  updateUserModule: (userId, moduleKey, data) => apiClient.put(`${API}/access/users/${userId}/modules/${moduleKey}`, data),
  
  // User Capabilities
  getUserCapabilities: (userId) => apiClient.get(`${API}/access/users/${userId}/capabilities`),
  updateUserCapability: (userId, capKey, data) => apiClient.put(`${API}/access/users/${userId}/capabilities/${capKey}`, data),
  deleteUserCapability: (userId, capKey) => apiClient.delete(`${API}/access/users/${userId}/capabilities/${capKey}`),
  
  // User Employee Links
  getUserEmployeeLinks: (userId) => apiClient.get(`${API}/access/users/${userId}/employee-links`),
  createUserEmployeeLink: (userId, data) => apiClient.post(`${API}/access/users/${userId}/employee-links`, data),
  deleteUserEmployeeLink: (userId, linkId) => apiClient.delete(`${API}/access/users/${userId}/employee-links/${linkId}`),
  
  // Reporting Lines
  getReportingLines: (params) => apiClient.get(`${API}/access/reporting-lines`, { params }),
  createReportingLine: (data) => apiClient.post(`${API}/access/reporting-lines`, data),
  
  // Resolved Access API (THE CRITICAL CONTRACT)
  getMyAccess: (moduleKey) => apiClient.get(`${API}/access/me/modules/${moduleKey}`),
  getResolvedAccess: (userId, moduleKey) => apiClient.get(`${API}/access/users/${userId}/modules/${moduleKey}/resolved`),
  
  // Migration
  migrateUsersToOutlets: () => apiClient.post(`${API}/access/migrate/users-to-outlets`),

  // =========================================================================
  // ACCOUNTS DELETION GOVERNANCE (Same pattern as Vendor deletions)
  // =========================================================================
  getAccountsDeleteRequests: (status, orgId, outletId) => {
    const headers = {};
    if (orgId) headers['X-Org-Id'] = orgId;
    if (outletId) headers['X-Outlet-Id'] = outletId;
    return apiClient.get(`${API}/accounts-delete-requests`, { params: { status }, headers });
  },
  getAccountsDeleteRequestDetail: (requestId, orgId, outletId) => {
    const headers = {};
    if (orgId) headers['X-Org-Id'] = orgId;
    if (outletId) headers['X-Outlet-Id'] = outletId;
    return apiClient.get(`${API}/accounts-delete-requests/${requestId}`, { headers });
  },
  previewAccountsDeleteRequest: (data, orgId, outletId) => {
    const headers = {};
    if (orgId) headers['X-Org-Id'] = orgId;
    if (outletId) headers['X-Outlet-Id'] = outletId;
    return apiClient.post(`${API}/accounts-delete-requests/preview`, data, { headers });
  },
  createAccountsDeleteRequest: (data, orgId, outletId) => {
    const headers = {};
    if (orgId) headers['X-Org-Id'] = orgId;
    if (outletId) headers['X-Outlet-Id'] = outletId;
    return apiClient.post(`${API}/accounts-delete-requests`, data, { headers });
  },
  approveAccountsDeleteRequest: (requestId, orgId, outletId) => {
    const headers = {};
    if (orgId) headers['X-Org-Id'] = orgId;
    if (outletId) headers['X-Outlet-Id'] = outletId;
    return apiClient.post(`${API}/accounts-delete-requests/${requestId}/approve`, {}, { headers });
  },
  rejectAccountsDeleteRequest: (requestId, rejectionReason, orgId, outletId) => {
    const headers = {};
    if (orgId) headers['X-Org-Id'] = orgId;
    if (outletId) headers['X-Outlet-Id'] = outletId;
    return apiClient.post(`${API}/accounts-delete-requests/${requestId}/reject`, { rejection_reason: rejectionReason }, { headers });
  },
  cancelAccountsDeleteRequest: (requestId, orgId, outletId) => {
    const headers = {};
    if (orgId) headers['X-Org-Id'] = orgId;
    if (outletId) headers['X-Outlet-Id'] = outletId;
    return apiClient.post(`${API}/accounts-delete-requests/${requestId}/cancel`, {}, { headers });
  },
  executeAccountsDeleteRequest: (requestId, orgId, outletId) => {
    const headers = {};
    if (orgId) headers['X-Org-Id'] = orgId;
    if (outletId) headers['X-Outlet-Id'] = outletId;
    return apiClient.post(`${API}/accounts-delete-requests/${requestId}/execute`, {}, { headers });
  },

  // =========================================================================
  // CASH INTELLIGENCE — RETIRED from Control Room.
  // Cash authority: SALES (collections/refunds), BILLS (cash expense outflows),
  // ACCOUNTS (physical counts, verified position, expected cash, variance).
  // =========================================================================

  // Seed Data
  seedData: () => apiClient.post(`${API}/seed-data`),

  // Data Sync (External API Integration) — READ-ONLY
  // triggerSync RETIRED — the Expense Capture mirror is gone; Bills is read on demand.
  getSyncHistory: () => apiClient.get(`${API}/sync/history`),
  getSyncStatus: () => apiClient.get(`${API}/sync/status`),

  // Intelligence (READ-ONLY interpretation layer)
  // Expense intelligence RETIRED — operating expenses are owned by 1OS Bills,
  // cash position/variance by Accounts.

  // Data Quality Alerts (structured alert engine)
  getStructuredAlerts: (params) => apiClient.get(`${API}/data-quality/alerts`, { params }),
  getDataCoverage: () => apiClient.get(`${API}/data-quality/coverage`),
  // getDuplicateIntelligence RETIRED — Supplier Bill duplicate resolution is owned by 1OS Bills.
  getCostStructure: () => apiClient.get(`${API}/intelligence/cost-structure`),
  getSourceConflicts: (params) => apiClient.get(`${API}/intelligence/source-conflicts`, { params }),
  getTimeIntegrity: (params) => apiClient.get(`${API}/intelligence/time-integrity`, { params }),

  // Vendor Category Master
  getVendorCategories: (params) => apiClient.get(`${API}/master-data/vendor-categories`, { params }),
  createVendorCategory: (data) => apiClient.post(`${API}/master-data/vendor-categories`, data),
  updateVendorCategory: (id, data) => apiClient.put(`${API}/master-data/vendor-categories/${id}`, data),
  toggleVendorCategoryActive: (id) => apiClient.patch(`${API}/master-data/vendor-categories/${id}/activate`),

  // Item Category Master
  getItemCategories: (params) => apiClient.get(`${API}/master-data/item-categories`, { params }),
  createItemCategory: (data) => apiClient.post(`${API}/master-data/item-categories`, data),
  updateItemCategory: (id, data) => apiClient.put(`${API}/master-data/item-categories/${id}`, data),
  toggleItemCategoryActive: (id) => apiClient.patch(`${API}/master-data/item-categories/${id}/activate`),

  // Ingredient Identity Resolution
  resolveIngredient: (data) => apiClient.post(`${API}/canonical-ingredients/resolve`, data),
  resolveIngredientBatch: (data) => apiClient.post(`${API}/canonical-ingredients/resolve-batch`, data),

  // Governance Signals
  getGovernanceSignals: (params) => apiClient.get(`${API}/governance/signals`, { params }),
  getGovernanceModules: () => apiClient.get(`${API}/governance/modules`),
  ingestGovernanceSignal: (data) => apiClient.post(`${API}/governance/signals`, data),
};
